import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import QRCode from 'qrcode';
import { Calendar, CalendarPlus, CheckCircle2, Copy, MapPin, Ticket } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Img } from '@/components/ui';
import { useEvents } from '@/context/EventContext';
import { formatINR } from '@/lib/catalog';
import { buildBookingIcs, downloadIcs, wallClockFrom } from '@/lib/bookings';
import { track } from '@/lib/analytics';
import KuickoRewardCard from '@/components/rewards/KuickoRewardCard';

const parseAnyDate = (value) => {
  if (!value) return null;
  const iso = parseISO(String(value));
  if (isValid(iso)) return iso;
  const fallback = new Date(value);
  return isValid(fallback) ? fallback : null;
};

const BookingConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingDetails: fromContext } = useEvents();
  const [qr, setQr] = useState('');

  const booking = location.state || fromContext;
  const event = booking?.event;

  useEffect(() => {
    if (!booking) navigate('/');
  }, [booking, navigate]);

  // The end of the funnel. Keyed on the booking id so a refresh of the
  // confirmation page doesn't record a second conversion.
  useEffect(() => {
    const bookingId = booking?.bookingId || booking?.bookingCode;
    if (!bookingId) return;
    track('booking_confirmed', {
      bookingId,
      serviceId: event?.id ?? null,
      providerId: event?.providerId ?? null,
    });
  }, [booking?.bookingId, booking?.bookingCode]);

  useEffect(() => {
    const value = booking?.bookingCode || booking?.bookingId;
    if (!value) return;
    QRCode.toDataURL(String(value), { margin: 1, width: 320 })
      .then(setQr)
      .catch(() => setQr(''));
  }, [booking]);

  const ticketCount = useMemo(() => {
    if (Array.isArray(booking?.tickets)) {
      return booking.tickets.reduce((sum, t) => sum + (t.quantity || 1), 0);
    }
    return Number(booking?.tickets) || 1;
  }, [booking]);

  if (!booking || !event) return null;

  const eventDate = parseAnyDate(event.date || booking.selectedDate);
  const when = eventDate
    ? `${format(eventDate, 'EEEE, d MMM yyyy')}${event.time ? ` · ${event.time}` : ''}`
    : event.time || 'Date to be announced';

  // Right after purchase is when people actually save the date. Built from the
  // chosen day + the event's start time (IST wall clock), the same shape the
  // booking itself was created with.
  const startWall = wallClockFrom(event.date || booking.selectedDate, event.time || booking.selectedTime);
  const calendarIcs = startWall
    ? buildBookingIcs(
        {
          id: booking.bookingId || booking.bookingCode || 'new',
          code: booking.bookingCode || null,
          title: event.title || 'Event',
          date: startWall,
          venue: event.venue || '',
          ticketCount,
        },
        {
          url: booking.bookingId ? `${window.location.origin}/booking/${booking.bookingId}` : undefined,
        }
      )
    : null;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(String(booking.bookingCode || booking.bookingId));
      toast.success('Booking code copied');
    } catch {
      toast.error('Could not copy the code');
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />

      <main className="flex-1 py-10 sm:py-14">
        {/* CSS entrance only — the confirmation (and its QR code) must be
            visible even if a JS animation never runs. */}
        <div className="mx-auto max-w-2xl animate-floatUp px-4 sm:px-6">
          <div className="mb-8 text-center">
            <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-tint ring-8 ring-success-tint/50">
              <CheckCircle2 className="h-9 w-9 text-success" aria-hidden="true" />
            </span>
            <h1 className="text-h1 text-ink-900">Booking confirmed</h1>
            <p className="mt-2 text-sm text-ink-500">
              Your tickets are ready. We've also saved them to My Bookings.
            </p>
          </div>

          {/* Ticket stub */}
          <div className="lk-card overflow-hidden">
            <div className="flex gap-4 p-5 sm:p-6">
              <Img src={event.image} alt={event.title} className="h-28 w-[86px] shrink-0 rounded-xl" />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold leading-tight text-ink-900">
                  {event.title || 'Event'}
                </h2>
                <p className="mt-1.5 flex items-center gap-1.5 text-sm text-ink-700">
                  <Calendar className="h-4 w-4 shrink-0 text-ink-500" aria-hidden="true" />
                  {when}
                </p>
                {event.venue && (
                  <p className="mt-1 flex items-start gap-1.5 text-sm text-ink-500">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="line-clamp-2">{event.venue}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Perforation */}
            <div className="relative border-t border-dashed border-ink-200">
              <span className="absolute -left-3 -top-3 h-6 w-6 rounded-full bg-surface-muted" />
              <span className="absolute -right-3 -top-3 h-6 w-6 rounded-full bg-surface-muted" />
            </div>

            <div className="flex flex-col items-center gap-6 p-5 sm:flex-row sm:p-6">
              <div className="w-36 shrink-0">
                {qr ? (
                  <img src={qr} alt="Booking QR code" className="w-full rounded-xl" />
                ) : (
                  <div className="flex aspect-square items-center justify-center rounded-xl bg-surface-sunken text-xs text-ink-500">
                    Generating…
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1 text-center sm:text-left">
                {(booking.bookingCode || booking.bookingId) && (
                  <>
                    <p className="lk-label">
                      Booking code
                    </p>
                    <p className="tabular text-2xl font-bold tracking-wider text-ink-900">
                      {booking.bookingCode || booking.bookingId}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center justify-center gap-1 sm:justify-start">
                      <button
                        type="button"
                        onClick={copyCode}
                        className="lk-focus inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-brand-secondary hover:bg-brand-tint active:scale-95"
                      >
                        <Copy className="h-4 w-4" aria-hidden="true" />
                        Copy
                      </button>
                      {calendarIcs && (
                        <button
                          type="button"
                          onClick={() => downloadIcs(calendarIcs, event.title)}
                          className="lk-focus inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-brand-secondary hover:bg-brand-tint active:scale-95"
                        >
                          <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                          Add to calendar
                        </button>
                      )}
                    </div>
                  </>
                )}

                <div className="mt-4 flex items-center justify-center gap-6 sm:justify-start">
                  <div>
                    <p className="lk-label">Tickets</p>
                    <p className="font-semibold text-ink-900">{ticketCount}</p>
                  </div>
                  {booking.total != null && (
                    <div>
                      <p className="lk-label">Total paid</p>
                      <p className="font-semibold text-ink-900">₹{formatINR(booking.total)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Seat labels, when the event uses reserved seating */}
            {Array.isArray(booking.tickets) &&
              booking.tickets.some((t) => t.seatLabels?.length > 0) && (
                <div className="border-t border-ink-200/70 p-5 sm:p-6">
                  <p className="mb-2 text-sm font-semibold text-ink-900">Your seats</p>
                  <div className="space-y-2">
                    {booking.tickets.map((ticket, i) =>
                      ticket.seatLabels?.length > 0 ? (
                        <div key={i}>
                          <p className="mb-1 text-xs text-ink-500">{ticket.name}</p>
                          <div className="flex flex-wrap gap-1">
                            {ticket.seatLabels.map((label) => (
                              <span
                                key={label}
                                className="rounded bg-brand-tint px-2 py-0.5 text-xs font-semibold text-brand-secondary"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              )}
          </div>

          <p className="mt-4 text-center text-xs text-ink-500">
            Show this QR code with a valid photo ID at the venue entrance.
          </p>

          {/* Cross-app reward (LK → Kuicko). Renders nothing unless this
              booking actually earned one. */}
          {booking.bookingId && <KuickoRewardCard bookingId={booking.bookingId} />}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/my-bookings" className="lk-btn-primary flex-1">
              <Ticket className="h-4 w-4" />
              View my bookings
            </Link>
            <Link to="/" className="lk-btn-ghost flex-1">
              Explore more events
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookingConfirmationPage;
